import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { RosterPattern } from '@/components/RosterCalculator'

export interface RosterPreference {
  _id: string
  userId: string
  startDate: string
  monthsToShow: number
  activePattern: RosterPattern
  updatedAt: string
}

export function useRosterPreferences(userId: string | undefined) {
  const [preference, setPreference] = useState<RosterPreference | null>(null)
  const [loading, setLoading] = useState(false)

  const fetchPreference = useCallback(async () => {
    if (!userId) {
      setPreference(null)
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.roster_preferences.list({
        filter: { userId },
        limit: 1
      })
      setPreference((list[0] as RosterPreference) || null)
    } catch (error) {
      console.error('Failed to fetch roster preferences:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchPreference()
  }, [fetchPreference])

  const savePreference = async (data: {
    startDate: string
    monthsToShow: number
    activePattern: RosterPattern
  }) => {
    if (!userId) {
      throw new Error('Must be logged in to save preferences')
    }
    const now = new Date().toISOString()

    if (preference) {
      const updated = await lumi.entities.roster_preferences.update(preference._id, {
        ...data,
        updatedAt: now
      })
      setPreference(updated as RosterPreference)
      return updated
    }

    const created = await lumi.entities.roster_preferences.create({
      userId,
      ...data,
      updatedAt: now
    })
    setPreference(created as RosterPreference)
    return created
  }

  return { preference, loading, savePreference, refetch: fetchPreference }
}
